import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@package/subframe/components/Button";
import { Checkbox } from "@package/subframe/components/Checkbox";
import { IconButton } from "@package/subframe/components/IconButton";
import { Select } from "@package/subframe/components/Select";
import { Switch } from "@package/subframe/components/Switch";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherArrowLeft,
  FeatherCalendar,
  FeatherPlus,
  FeatherTrash2,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNursery } from "@/contexts/NurseryContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import {
  useCreateTaskDefinitionCommandControllerCreate,
  useUpdateTaskDefinitionCommandControllerUpdate,
} from "@/generated/compliance-tasks/compliance-tasks";
import { useListNurseryMembersQueryControllerListMembers } from "@/generated/members/members";
import type { ComplianceTaskDefinitionResponseDto } from "@/generated/model/complianceTaskDefinitionResponseDto";
import {
  extractApiErrorMessage,
  invalidateComplianceQueries,
} from "@/lib/compliance/invalidate-compliance-queries";

import { FREQUENCY_OPTIONS, ROLE_DISPLAY } from "./compliance-constants";
import { buildComplianceTaskSummary } from "./compliance-task-summary";
import { ConfirmComplianceTaskDialog } from "./ConfirmComplianceTaskDialog";
import { buildExternalServiceFields } from "./external-service-fields";

// ─── Schema ──────────────────────────────────────────────────────────────────

const complianceTaskFormSchema = z.object({
  name: z.string().min(1, "Task name is required"),
  description: z.string().optional(),
  primaryCategoryId: z.string().min(1, "Select a primary category"),
  secondaryCategoryIds: z.array(z.string()),
  startDate: z.date(),
  frequency: z.string().min(1, "Select a repeat frequency"),
  assignmentType: z.enum(["role", "person"]),
  assigneeRole: z.string().optional(),
  assigneeUserId: z.string().optional(),
  evidenceRequired: z.boolean(),
  externalServiceFields: z
    .array(
      z.object({
        // Stable key the completion form reads back from externalServiceData.
        // Optional in the form (derived on save); present once round-tripped.
        name: z.string().optional(),
        label: z.string(),
        type: z.string(),
        required: z.boolean(),
      }),
    )
    .optional(),
});

type ComplianceTaskFormData = z.infer<typeof complianceTaskFormSchema>;

// ─── Component ───────────────────────────────────────────────────────────────

interface ComplianceTaskFormProps {
  mode: "create" | "edit";
  definition?: ComplianceTaskDefinitionResponseDto | null;
  isLoading?: boolean;
}

export function ComplianceTaskForm({
  mode,
  definition,
  isLoading,
}: ComplianceTaskFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedNurseryId, selectedNursery } = useNursery();

  const isEditMode = mode === "edit";
  // Where every exit from this form lands. Create returns to Open so the user
  // sees the occurrence they just spawned (NAU-373); edit returns to Tasks, the
  // definitions list it was launched from.
  const managerExitTab = isEditMode ? "tasks" : "open";
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Confirmation step (NAU-336): a valid create/edit submission is held here
  // and only persisted once the admin confirms. For create, the mode defaults
  // to "create"; for edit, it's "edit".
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingData, setPendingData] = useState<ComplianceTaskFormData | null>(
    null,
  );
  const [confirmMode, setConfirmMode] = useState<"create" | "edit">("create");

  const createMutation = useCreateTaskDefinitionCommandControllerCreate();
  const updateMutation = useUpdateTaskDefinitionCommandControllerUpdate();

  // Fetch nursery members for "assign to person" dropdown
  const { data: membersData } = useListNurseryMembersQueryControllerListMembers(
    { nurseryId: selectedNurseryId ?? undefined, limit: 100 },
    { query: { enabled: !!selectedNurseryId } },
  );
  const members =
    (membersData?.data as any)?.data ?? (membersData?.data as any) ?? [];

  // Active categories for the multi-select. Names are resolved here rather than
  // from a hardcoded map.
  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const categories = categoriesResponse?.data.items ?? [];

  const esConfig = definition?.externalServiceConfig as
    | {
        fields?: {
          name?: string;
          label: string;
          type?: string;
          required?: boolean;
        }[];
      }
    | undefined;

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ComplianceTaskFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(complianceTaskFormSchema) as any,
    defaultValues: {
      name: definition?.name ?? "",
      description: definition?.description ?? "",
      primaryCategoryId: definition?.primaryCategoryId ?? "",
      // Secondary = every assigned category except the primary. Legacy tasks
      // (no primary) start with an empty primary and all categories as secondary
      // until the user nominates one.
      secondaryCategoryIds: (definition?.categoryIds ?? []).filter(
        (id) => id !== definition?.primaryCategoryId,
      ),
      startDate: definition?.startDate
        ? new Date(definition.startDate)
        : undefined,
      // Pre-fill in edit mode; legacy tasks have no frequency, so the field
      // starts empty and the admin must choose one before saving (NAU-302).
      frequency: (definition?.frequency as string | undefined) ?? "",
      assignmentType: definition?.assigneeUserId ? "person" : "role",
      assigneeRole: definition?.assigneeRole ?? undefined,
      assigneeUserId: definition?.assigneeUserId ?? undefined,
      evidenceRequired: definition?.evidenceRequired ?? false,
      externalServiceFields:
        esConfig?.fields?.map((f) => ({
          name: f.name,
          label: f.label,
          type: f.type ?? "text",
          required: f.required ?? true,
        })) ?? [],
    },
  });

  const watchedName = watch("name");
  const watchedDescription = watch("description");
  const watchedPrimaryCategoryId = watch("primaryCategoryId");
  const watchedSecondaryCategoryIds = watch("secondaryCategoryIds");
  const watchedStartDate = watch("startDate");
  const watchedFrequency = watch("frequency");

  const toggleCategory = (categoryId: string, checked: boolean) => {
    const current = watchedSecondaryCategoryIds ?? [];
    setValue(
      "secondaryCategoryIds",
      checked
        ? [...current, categoryId]
        : current.filter((id) => id !== categoryId),
      { shouldValidate: true },
    );
  };

  const selectPrimaryCategory = (categoryId: string) => {
    // A category can't be both primary and secondary — promoting one to primary
    // removes it from the secondary set.
    setValue(
      "secondaryCategoryIds",
      (watchedSecondaryCategoryIds ?? []).filter((id) => id !== categoryId),
    );
    setValue("primaryCategoryId", categoryId, { shouldValidate: true });
  };
  const watchedAssignmentType = watch("assignmentType");
  const watchedAssigneeRole = watch("assigneeRole");
  const watchedAssigneeUserId = watch("assigneeUserId");
  const watchedEvidenceRequired = watch("evidenceRequired");
  const watchedExternalServiceFields = watch("externalServiceFields") ?? [];

  const onFormError = (fieldErrors: FieldErrors<ComplianceTaskFormData>) => {
    const firstKey = Object.keys(fieldErrors)[0];
    if (firstKey) {
      const el = document.getElementById(firstKey);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    toast.error("Please fix the errors before saving");
  };

  const persistTask = async (data: ComplianceTaskFormData) => {
    if (!selectedNurseryId) return;

    const fields = buildExternalServiceFields(data.externalServiceFields ?? []);
    const esConfig = fields.length > 0 ? { fields } : undefined;
    // completionMode is fixed at creation and immutable thereafter, so a task
    // with service detail fields must be created as EXTERNAL_SERVICE for the
    // completion screen to render those fields.
    const completionMode = fields.length > 0 ? "EXTERNAL_SERVICE" : "SIMPLE";

    // categoryIds is the full classification set: primary first, then the
    // secondaries. The API keeps the primary as part of categoryIds.
    const categoryIds = [data.primaryCategoryId, ...data.secondaryCategoryIds];

    try {
      if (isEditMode && definition) {
        await updateMutation.mutateAsync({
          definitionId: definition.id,
          data: {
            ...(data.startDate
              ? { startDate: format(data.startDate, "yyyy-MM-dd") }
              : {}),
            name: data.name,
            description: data.description || undefined,
            categoryIds,
            primaryCategoryId: data.primaryCategoryId,
            frequency: data.frequency as any,
            evidenceRequired: data.evidenceRequired,
            externalServiceConfig: esConfig as any,
            assigneeUserId:
              data.assignmentType === "person"
                ? data.assigneeUserId
                : (null as any),
            assigneeRole:
              data.assignmentType === "role"
                ? (data.assigneeRole as any)
                : (null as any),
          },
        });
        toast.success("Compliance task updated");
      } else {
        await createMutation.mutateAsync({
          nurseryId: selectedNurseryId,
          data: {
            name: data.name,
            description: data.description || undefined,
            categoryIds,
            primaryCategoryId: data.primaryCategoryId,
            startDate: format(data.startDate, "yyyy-MM-dd"),
            frequency: data.frequency as any,
            completionMode: completionMode as any,
            evidenceRequired: data.evidenceRequired,
            externalServiceConfig: esConfig as any,
            assigneeUserId:
              data.assignmentType === "person"
                ? data.assigneeUserId
                : undefined,
            assigneeRole:
              data.assignmentType === "role"
                ? (data.assigneeRole as any)
                : undefined,
          },
        });
        toast.success("Compliance task created");
      }

      // Creating a definition synchronously spawns its first instance, so the
      // Workspace to-do list and the Compliance Manager tables both change.
      // The shared helper covers the hand-rolled to-do list key that an
      // inlined request-path predicate misses.
      await invalidateComplianceQueries(queryClient);
      // Back to Compliance Manager, which is where Add task is launched from
      // and the only compliance screen the sidebar links to.
      navigate({ to: "/compliance-manager", search: { tab: managerExitTab } });
    } catch (error: unknown) {
      toast.error(
        extractApiErrorMessage(
          error,
          `Failed to ${isEditMode ? "update" : "create"} compliance task`,
        ),
      );
    }
  };

  // Both create and edit ask for confirmation (NAU-336).
  const onSubmit = (data: ComplianceTaskFormData) => {
    setPendingData(data);
    setConfirmMode(isEditMode ? "edit" : "create");
    setConfirmOpen(true);
  };

  const handleConfirmTaskAction = () => {
    if (!pendingData) return;
    void persistTask(pendingData);
  };

  // ─── External service field helpers ──────────────────────────────────────

  const addExternalServiceField = () => {
    setValue("externalServiceFields", [
      ...watchedExternalServiceFields,
      { label: "", type: "text", required: true },
    ]);
  };

  const removeExternalServiceField = (index: number) => {
    setValue(
      "externalServiceFields",
      watchedExternalServiceFields.filter((_, i) => i !== index),
    );
  };

  const updateExternalServiceField = (
    index: number,
    field: "label" | "type",
    value: string,
  ) => {
    const updated = watchedExternalServiceFields.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    setValue("externalServiceFields", updated);
  };

  // ─── Loading / Not Found ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (isEditMode && !definition) {
    return (
      <div className="flex w-full flex-col items-start gap-8 bg-white animate-fade-in">
        <div className="flex w-full flex-col items-start gap-1 px-8 py-8">
          <div className="flex w-full items-center gap-2">
            <IconButton
              icon={<FeatherArrowLeft />}
              onClick={() =>
                navigate({
                  to: "/compliance-manager",
                  search: { tab: managerExitTab },
                })
              }
            />
            <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
              Task not found
            </span>
          </div>
          <span className="text-body font-body text-subtext-color">
            The compliance task you&apos;re looking for doesn&apos;t exist.
          </span>
        </div>
      </div>
    );
  }

  const isSaving =
    isSubmitting || createMutation.isPending || updateMutation.isPending;

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="flex w-full max-w-4xl flex-col items-start bg-white animate-fade-in">
      <form
        onSubmit={handleSubmit(onSubmit as any, onFormError as any)}
        className="flex w-full flex-col items-start"
      >
        {/* Header */}
        <div className="flex w-full flex-col items-start gap-1 pb-8">
          <div className="flex w-full flex-wrap items-center gap-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <div className="flex w-full items-center gap-2">
                <IconButton
                  icon={<FeatherArrowLeft />}
                  onClick={() =>
                    navigate({
                      to: "/compliance-manager",
                      search: { tab: managerExitTab },
                    })
                  }
                />
                <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                  {isEditMode
                    ? "Edit compliance task"
                    : "Create compliance task"}
                </span>
              </div>
              <span className="text-body font-body text-subtext-color">
                {isEditMode
                  ? "Update the compliance task definition"
                  : "Set up a new compliance task for your nursery"}
              </span>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="neutral-primary"
                size="large"
                onClick={() =>
                  navigate({
                    to: "/compliance-manager",
                    search: { tab: managerExitTab },
                  })
                }
              >
                Cancel
              </Button>
              <Button type="submit" size="large" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          {/* ── Section: Task Details ─────────────────────────────────────── */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-7">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Task details
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  Name and classify the compliance task
                </span>
              </div>
            </div>
            <div className="flex max-w-lg grow shrink-0 basis-0 flex-col items-start gap-6">
              <TextField
                className="h-auto w-full flex-none"
                size="large"
                label="Task name"
                error={!!errors.name}
                helpText={errors.name?.message}
              >
                <TextField.Input
                  id="name"
                  placeholder="e.g. Fire Door Inspection"
                  value={watchedName}
                  onChange={(e) => setValue("name", e.target.value)}
                />
              </TextField>

              <TextArea
                className="h-auto w-full flex-none"
                label="Description / guidance notes"
                helpText="Instructions for the person completing this task"
              >
                <TextArea.Input
                  id="description"
                  placeholder="Describe what needs to be checked and how..."
                  value={watchedDescription}
                  onChange={(e) => setValue("description", e.target.value)}
                />
              </TextArea>

              <div
                className="flex w-full flex-col items-start gap-2"
                id="primaryCategoryId"
              >
                <Select
                  className="h-auto w-full flex-none"
                  label="Primary category"
                  placeholder="Select a primary category"
                  helpText={errors.primaryCategoryId?.message}
                  error={!!errors.primaryCategoryId}
                  value={watchedPrimaryCategoryId || undefined}
                  onValueChange={selectPrimaryCategory}
                >
                  {categories.map((category) => (
                    <Select.Item key={category.id} value={category.id}>
                      {category.name}
                    </Select.Item>
                  ))}
                </Select>
                <span className="text-caption font-caption text-subtext-color">
                  The main classification for this task.
                </span>
              </div>

              <div
                className="flex w-full flex-col items-start gap-2"
                id="secondaryCategoryIds"
              >
                <span className="text-body-bold font-body-bold text-default-font">
                  Secondary categories
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Optionally classify this task under additional categories.
                </span>
                <div className="w-full columns-2 gap-x-4 pt-1">
                  {categories
                    .filter(
                      (category) => category.id !== watchedPrimaryCategoryId,
                    )
                    .map((category) => (
                      <div
                        key={category.id}
                        className="mb-2 break-inside-avoid"
                      >
                        <Checkbox
                          label={category.name}
                          checked={(watchedSecondaryCategoryIds ?? []).includes(
                            category.id,
                          )}
                          onCheckedChange={(checked) =>
                            toggleCategory(category.id, checked)
                          }
                        />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Section: Scheduling ───────────────────────────────────────── */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-7">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Scheduling
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  When this task is first due. The next due date is set on
                  completion (e.g. from the certificate expiry).
                </span>
              </div>
            </div>
            <div className="flex max-w-lg grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  First due date
                </span>
                {isEditMode && definition?.canEditStartDate === false ? (
                  <>
                    <div className="flex h-10 w-full items-center gap-2 rounded-md border border-solid border-secondary-beige-200 bg-neutral-50 px-3 text-sm">
                      <FeatherCalendar className="h-4 w-4 text-subtext-color" />
                      <span className="text-neutral-900">
                        {watchedStartDate
                          ? format(watchedStartDate, "d MMM yyyy")
                          : "—"}
                      </span>
                    </div>
                    <span className="text-caption font-caption text-subtext-color">
                      Start date can’t be changed once the first instance has
                      been completed.
                    </span>
                  </>
                ) : (
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        id="startDate"
                        className={`flex h-10 w-full items-center gap-2 rounded-md border border-solid px-3 text-sm ${
                          errors.startDate
                            ? "border-error-600"
                            : "border-secondary-beige-200"
                        } bg-white hover:bg-neutral-50`}
                      >
                        <FeatherCalendar className="h-4 w-4 text-subtext-color" />
                        <span
                          className={
                            watchedStartDate
                              ? "text-neutral-900"
                              : "text-subtext-color"
                          }
                        >
                          {watchedStartDate
                            ? format(watchedStartDate, "d MMM yyyy")
                            : "Select a date"}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={watchedStartDate}
                        onSelect={(date) => {
                          if (date) {
                            setValue("startDate", date);
                            setCalendarOpen(false);
                          }
                        }}
                        disabled={{
                          before: new Date(new Date().setHours(0, 0, 0, 0)),
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
                {!isEditMode && errors.startDate && (
                  <span className="text-caption font-caption text-error-600">
                    {errors.startDate.message}
                  </span>
                )}
              </div>

              <div
                className="flex w-full flex-col items-start gap-2"
                id="frequency"
              >
                <Select
                  className="h-auto w-full flex-none"
                  label="Repeat frequency"
                  placeholder="Select a frequency"
                  helpText={errors.frequency?.message}
                  error={!!errors.frequency}
                  value={watchedFrequency || undefined}
                  onValueChange={(val) =>
                    setValue("frequency", val, { shouldValidate: true })
                  }
                >
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <Select.Item key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Item>
                  ))}
                </Select>
                <span className="text-caption font-caption text-subtext-color">
                  How often this task repeats. On completion, the next due date
                  must fall within a window measured from this frequency.
                  {isEditMode &&
                    " Changing it affects future occurrences only — the currently open instance keeps its due date."}
                </span>
              </div>
            </div>
          </div>

          {/* ── Section: Responsibility ───────────────────────────────────── */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-7">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Responsibility
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  Assign this task to a role or a specific person
                </span>
              </div>
            </div>
            <div className="flex max-w-lg grow shrink-0 basis-0 flex-col items-start gap-6">
              <Tabs
                value={watchedAssignmentType}
                onValueChange={(val) => {
                  setValue("assignmentType", val as "role" | "person");
                  setValue("assigneeRole", undefined);
                  setValue("assigneeUserId", undefined);
                }}
                className="w-full"
              >
                <TabsList>
                  <TabsTrigger value="role">A role</TabsTrigger>
                  <TabsTrigger value="person">A specific person</TabsTrigger>
                </TabsList>
                <TabsContent value="role" className="pt-4">
                  <Select
                    className="h-auto w-full max-w-[49%] flex-none"
                    label="Role"
                    placeholder="Select a role"
                    helpText={
                      watchedAssignmentType === "role"
                        ? errors.assigneeRole?.message
                        : undefined
                    }
                    error={
                      watchedAssignmentType === "role" && !!errors.assigneeRole
                    }
                    value={watchedAssigneeRole}
                    onValueChange={(val) => setValue("assigneeRole", val)}
                  >
                    {Object.entries(ROLE_DISPLAY).map(([value, label]) => (
                      <Select.Item key={value} value={value}>
                        {label}
                      </Select.Item>
                    ))}
                  </Select>
                </TabsContent>
                <TabsContent value="person" className="pt-4">
                  <Select
                    className="h-auto w-full max-w-[49%] flex-none"
                    label="Staff member"
                    placeholder="Select a person"
                    helpText={
                      watchedAssignmentType === "person"
                        ? errors.assigneeUserId?.message
                        : undefined
                    }
                    error={
                      watchedAssignmentType === "person" &&
                      !!errors.assigneeUserId
                    }
                    value={watchedAssigneeUserId}
                    onValueChange={(val) => setValue("assigneeUserId", val)}
                  >
                    {Array.isArray(members) &&
                      members.map((m: any) => (
                        <Select.Item
                          key={m.userId ?? m.id}
                          value={m.userId ?? m.id}
                        >
                          {m.user?.firstName ?? m.firstName}{" "}
                          {m.user?.lastName ?? m.lastName}
                        </Select.Item>
                      ))}
                  </Select>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* ── Section: Additional Settings ──────────────────────────────── */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-7">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Additional settings
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  Configure evidence requirements and service details
                </span>
              </div>
            </div>
            <div className="flex max-w-lg grow shrink-0 basis-0 flex-col items-start gap-6">
              {/* Evidence toggle */}
              <div className="flex w-full items-center justify-between rounded-md border border-solid border-secondary-beige-200 px-6 py-6">
                <div className="flex flex-col items-start gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Evidence upload required
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    When enabled, a file upload is required to complete this
                    task
                  </span>
                </div>
                <Switch
                  checked={watchedEvidenceRequired}
                  onCheckedChange={(checked) =>
                    setValue("evidenceRequired", checked)
                  }
                />
              </div>

              {/* External service fields */}
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full flex-col items-start gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Service detail fields
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Define fields that must be filled in when this task is
                    completed by an external contractor
                  </span>
                </div>

                {watchedExternalServiceFields.length === 0 ? (
                  <div className="flex w-full flex-col items-center gap-4 rounded-md border border-dashed border-neutral-300 px-8 py-8">
                    <span className="text-body font-body text-subtext-color text-center">
                      No fields defined. Add fields that staff must fill in when
                      completing this task.
                    </span>
                    <Button
                      type="button"
                      variant="neutral-secondary"
                      icon={<FeatherPlus />}
                      onClick={addExternalServiceField}
                    >
                      Add field
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex w-full flex-col items-start gap-2">
                      {watchedExternalServiceFields.map((field, index) => (
                        <div
                          key={index}
                          className="flex w-full items-end gap-3 rounded-md border border-solid border-secondary-beige-200 bg-neutral-50 px-4 py-3"
                        >
                          <TextField
                            className="h-auto grow flex-none"
                            label={index === 0 ? "Field name" : ""}
                          >
                            <TextField.Input
                              placeholder="e.g. Contractor Name"
                              value={field.label}
                              onChange={(e) =>
                                updateExternalServiceField(
                                  index,
                                  "label",
                                  e.target.value,
                                )
                              }
                            />
                          </TextField>
                          <Select
                            className="h-auto w-32 flex-none"
                            label={index === 0 ? "Type" : ""}
                            value={field.type}
                            onValueChange={(val) =>
                              updateExternalServiceField(index, "type", val)
                            }
                          >
                            <Select.Item value="text">Text</Select.Item>
                            <Select.Item value="number">Number</Select.Item>
                            <Select.Item value="date">Date</Select.Item>
                            <Select.Item value="yes_no">Yes/No</Select.Item>
                          </Select>
                          <IconButton
                            variant="neutral-tertiary"
                            icon={<FeatherTrash2 />}
                            onClick={() => removeExternalServiceField(index)}
                          />
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="neutral-secondary"
                      icon={<FeatherPlus />}
                      onClick={addExternalServiceField}
                    >
                      Add field
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer actions — mirror of the header Cancel / Save */}
          <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-secondary-beige-200 py-6">
            <Button
              variant="neutral-primary"
              size="large"
              onClick={() =>
                navigate({
                  to: "/compliance-manager",
                  search: { tab: managerExitTab },
                })
              }
            >
              Cancel
            </Button>
            <Button type="submit" size="large" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
      </form>

      {pendingData && (
        <ConfirmComplianceTaskDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          summary={buildComplianceTaskSummary(pendingData, {
            categories,
            members,
            nurseryName: selectedNursery?.name,
          })}
          isSaving={
            confirmMode === "create"
              ? createMutation.isPending
              : updateMutation.isPending
          }
          onConfirm={handleConfirmTaskAction}
          mode={confirmMode}
        />
      )}
    </div>
  );
}
